<script lang="ts">
  import {
    Pacientes, Notas, Signos, FarmaciaClinica, Ingresos, Laboratorio, Imagenologia, Citas, Hospital, Triajes, Farmacia, Episodios,
    downloadAuthenticatedFile,
    type Paciente, type Nota, type Signo, type Receta, type RecetaItem, type Ingreso, type Triaje, type Medicamento,
    type BorradorConsultaIa,
  } from '../lib/api';
  import ConsultaIaPanel from './ConsultaIaPanel.svelte';
  import { session } from '../lib/auth.svelte';
  import { tenantCtx } from '../lib/tenant.svelte';
  import { imprimirHtml, abrirOImprimirHtml } from '../lib/print-html';
  import { mensajeRecetaWhatsApp, telefonoWhatsAppGt } from '../lib/receta-share';
  import { resolveMedicoId, resolveMedicoProfesional } from '../lib/medico-session';
  import { clinicPreview, effectiveOrgTipo } from '../lib/clinic-preview.svelte';
  import { isPilotMode } from '../lib/pilot';
  import { isDemoTenant } from '../lib/clinic-preview.svelte';
  import {
    EXPEDIENTE_CUI_STORAGE,
    demoCuiPrincipal,
    demoExpedienteHints,
    DEMO_CUIS,
  } from '../lib/clinic-demo-seed';
  import {
    autorFisioterapista,
    isClinicaFisioterapia,
    NOTA_FISIO_PLACEHOLDER,
    SIGNOS_FISIO_LABELS,
  } from '../lib/fisioterapia';
  import {
    EJERCICIO_VACIO,
    PLANTILLAS_EJERCICIO_FISIO,
    RUTINA_FISIO_TIPO,
    formatRutinaWhatsApp,
    notasEvolucionClinica,
    rutinaMasReciente,
    rutinaVacia,
    serializeRutina,
    whatsappEnlaceRutina,
    type RutinaFisio,
  } from '../lib/fisioterapia-rutina';
  import { evaluacionesDesdeNotas } from '../lib/fisioterapia-evaluaciones';
  import FisioterapiaEvaluacionesPanel from './FisioterapiaEvaluacionesPanel.svelte';
  import {
    listPlacas, addPlaca, removePlaca,
    type PlacaDental, type TipoPlacaDental,
  } from '../lib/expediente-rx';
  import PacientePicker from './PacientePicker.svelte';
  import { edadDesdeNacimiento } from '../lib/csv-import';
  import {
    estadoLabLabel,
    estadoImagenLabel,
    estadoLabHint,
    estadoImagenHint,
  } from '../lib/estudio-estados';
  import { fichaDesdeMotivo, motivoDesdeFicha } from '../lib/motivo-sync';
  import { listAdjuntos, addAdjunto, removeAdjunto, type AdjuntoExpediente } from '../lib/expediente-adjuntos';

  const orgTipo = $derived(
    effectiveOrgTipo(
      tenantCtx.orgTipo as import('../lib/navigation').OrgTipo | null,
      tenantCtx.isHospitalEdition,
    ) ?? clinicPreview.org,
  );
  const esDental = $derived(orgTipo === 'clinica-odontologica');
  const esFisio = $derived(isClinicaFisioterapia(orgTipo));
  const esHospital = $derived(tenantCtx.isHospitalEdition || orgTipo === 'hospital-privado');
  const esDemo = $derived(isPilotMode() || isDemoTenant(session.value?.tenantId));
  const demoHint = $derived(
    esDemo ? demoExpedienteHints(clinicPreview.org ?? tenantCtx.orgTipo) : null,
  );

  const CUI_STORAGE = EXPEDIENTE_CUI_STORAGE;

  let pickerPacienteId = $state('');
  let cuiInput = $state('');
  let paciente = $state<Paciente | null>(null);
  let buscando = $state(false);
  let error = $state<string | null>(null);
  let info = $state<string | null>(null);
  let subTab = $state<'resumen' | 'ficha' | 'evolucion' | 'signos' | 'diagnostico' | 'medicamentos' | 'rutina' | 'placas' | 'receta' | 'citas' | 'altas' | 'adjuntos' | 'postop'>('resumen');

  let notas = $state<Nota[]>([]);
  let notasClinicas = $derived(notasEvolucionClinica(notas));
  let evaluacionesFisio = $derived(evaluacionesDesdeNotas(notas));
  let rutinaEdit = $state<RutinaFisio>(rutinaVacia());
  let rutinaVersiones = $derived(notas.filter(n => n.tipo === RUTINA_FISIO_TIPO).length);
  let signos = $state<Signo[]>([]);
  let ingresos = $state<Ingreso[]>([]);
  let placas = $state<PlacaDental[]>([]);
  let adjuntos = $state<AdjuntoExpediente[]>([]);
  let adjuntoNuevo = $state({ titulo: '', origen: 'EXTERNO' as const, tipo: 'REPORTE' as AdjuntoExpediente['tipo'], nota: '', archivoNombre: '', dataUrl: '' });
  let postop = $state({ fechaHora: '', evolucion: '', medicamento: '' });
  let cargandoDetalle = $state(false);

  let mostrarAlta = $state(false);
  let nuevoPac = $state<Paciente>({
    nombres: '', apellidos: '', cui: '', sexo: 'M', telefono: '', fechaNacimiento: '',
  });

  let notaNueva = $state({ autor: '', contenido: '' });
  const SpeechRecognition = typeof window !== 'undefined'
    ? (window as any).SpeechRecognition ?? (window as any).webkitSpeechRecognition
    : null;
  let dictando = $state(false);
  let dictandoCampo = $state<string | null>(null);

  function toggleDictado() {
    if (!SpeechRecognition) { error = 'El dictado por voz no está disponible en este navegador.'; return; }
    if (dictando) { dictando = false; return; }
    const rec = new SpeechRecognition();
    rec.lang = 'es-GT'; rec.interimResults = false;
    rec.onresult = (ev: any) => {
      const txt = ev.results[0][0].transcript;
      notaNueva.contenido = (notaNueva.contenido ? notaNueva.contenido + ' ' : '') + txt;
      dictando = false;
    };
    rec.onerror = () => { dictando = false; };
    rec.onend = () => { dictando = false; };
    dictando = true; rec.start();
  }

  function toggleDictadoFicha(campo: keyof typeof fichaEdit) {
    if (!SpeechRecognition) { error = 'El dictado por voz no está disponible en este navegador.'; return; }
    if (dictandoCampo === campo) { dictandoCampo = null; return; }
    const rec = new SpeechRecognition();
    rec.lang = 'es-GT'; rec.interimResults = false;
    rec.onresult = (ev: any) => {
      const txt = ev.results[0][0].transcript;
      const prev = fichaEdit[campo] ?? '';
      fichaEdit = { ...fichaEdit, [campo]: (prev ? prev + ' ' : '') + txt };
      dictandoCampo = null;
    };
    rec.onerror = () => { dictandoCampo = null; };
    rec.onend = () => { dictandoCampo = null; };
    dictandoCampo = campo;
    rec.start();
  }

  let signoNuevo = $state<Signo>({
    pacienteId: '', ta: '', fc: null, fr: null, temp: null, satO2: null, peso: null, talla: null,
  });

  let fichaEdit = $state({
    antecedentesPersonales: '', antecedentesFamiliares: '', habitosToxicos: '',
    alergias: '', antecedentesQuirurgicos: '',
    motivoConsulta: '', historiaEnfermedadActual: '', diagnostico: '',
  });
  let guardandoFicha = $state(false);

  function cargarFicha(p: Paciente, episodioAbierto?: { motivo?: string | null }) {
    const desdeEp = episodioAbierto?.motivo?.trim() ? fichaDesdeMotivo(episodioAbierto.motivo) : null;
    fichaEdit = {
      antecedentesPersonales: p.antecedentesPersonales ?? '',
      antecedentesFamiliares: p.antecedentesFamiliares ?? '',
      habitosToxicos: p.habitosToxicos ?? '',
      alergias: p.alergias ?? '',
      antecedentesQuirurgicos: p.antecedentesQuirurgicos ?? '',
      motivoConsulta: p.motivoConsulta ?? desdeEp?.motivoConsulta ?? '',
      historiaEnfermedadActual: p.historiaEnfermedadActual ?? desdeEp?.historiaEnfermedadActual ?? '',
      diagnostico: p.diagnostico ?? '',
    };
  }

  /** Solo rellena campos con texto del borrador; no guarda (sigue el botón Guardar). */
  function aplicarBorradorIa(b: BorradorConsultaIa) {
    const pick = (v?: string) => (v ?? '').trim();
    fichaEdit = {
      antecedentesPersonales: pick(b.antecedentesPersonales) || fichaEdit.antecedentesPersonales,
      antecedentesFamiliares: pick(b.antecedentesFamiliares) || fichaEdit.antecedentesFamiliares,
      habitosToxicos: pick(b.habitosToxicos) || fichaEdit.habitosToxicos,
      alergias: pick(b.alergias) || fichaEdit.alergias,
      antecedentesQuirurgicos: pick(b.antecedentesQuirurgicos) || fichaEdit.antecedentesQuirurgicos,
      motivoConsulta: pick(b.motivoConsulta) || fichaEdit.motivoConsulta,
      historiaEnfermedadActual: pick(b.historiaEnfermedadActual) || fichaEdit.historiaEnfermedadActual,
      diagnostico: pick(b.diagnostico) || fichaEdit.diagnostico,
    };
    info = 'Borrador IA aplicado a la ficha. Revise y pulse «Guardar ficha clínica» para persistir.';
  }

  async function guardarFicha(e: SubmitEvent) {
    e.preventDefault();
    if (!paciente?.id) return;
    guardandoFicha = true;
    error = null;
    try {
      const p = await Pacientes.actualizar(paciente.id, { ...paciente, ...fichaEdit });
      paciente = p;
      const motivoEp = motivoDesdeFicha(fichaEdit);
      if (episodioAbiertoId && motivoEp) {
        await Episodios.actualizar(episodioAbiertoId, { motivo: motivoEp });
      }
      if (!recetaDiagnostico.trim()) recetaDiagnostico = fichaEdit.diagnostico ?? '';
      info = 'Ficha clínica guardada.';
      await cargarExpediente(p);
    } catch (err: any) {
      error = err?.message ?? String(err);
    } finally {
      guardandoFicha = false;
    }
  }

  async function guardarDiagnostico(e: SubmitEvent) {
    e.preventDefault();
    if (!paciente?.id) return;
    guardandoFicha = true;
    error = null;
    try {
      const p = await Pacientes.actualizar(paciente.id, { ...paciente, diagnostico: fichaEdit.diagnostico });
      paciente = p;
      if (!recetaDiagnostico.trim()) recetaDiagnostico = fichaEdit.diagnostico ?? '';
      info = 'Diagnóstico guardado.';
      await cargarExpediente(p);
    } catch (err: any) {
      error = err?.message ?? String(err);
    } finally {
      guardandoFicha = false;
    }
  }

  const TIPOS_PLACA: { value: TipoPlacaDental; label: string }[] = [
    { value: 'PERIAPICAL', label: 'Periapical' },
    { value: 'BITEWING', label: 'Bitewing' },
    { value: 'PANORAMICA', label: 'Panorámica' },
    { value: 'CBCT', label: 'Tomografía (CBCT)' },
    { value: 'OTRO', label: 'Otra' },
  ];

  let placaNueva = $state({
    tipo: 'PERIAPICAL' as TipoPlacaDental,
    dientes: '',
    nota: '',
    medico: '',
    archivoNombre: '',
    imagenUrl: '',
  });

  let recetasPaciente = $state<Receta[]>([]);
  let recetaDiagnostico = $state('');
  let recetaItems = $state<RecetaItem[]>([]);
  let medicoActual = $state('');

  let labOrdenes = $state<any[]>([]);
  let imgOrdenes = $state<any[]>([]);
  let kardex = $state<any[]>([]);
  let indicacionesHospital = $state<any[]>([]);
  let triajesPaciente = $state<Triaje[]>([]);
  let medsHospital = $state<Medicamento[]>([]);
  let tratamientoNuevo = $state({
    medicamentoId: '',
    descripcion: '',
    dosis: '',
    via: 'IV',
    frecuencia: 'c/8h',
    horaProgramada: '',
  });
  let medBusqueda = $state('');
  let medPickerOpen = $state(false);
  let guardandoTratamiento = $state(false);

  function nombreMedicamento(k: any): string {
    if (k?.medicamentoNombre) return k.medicamentoNombre;
    const id = k?.medicamentoId;
    if (!id) return '—';
    return medsHospital.find(m => m.id === id)?.nombre
      ?? indicacionesHospital.find(i => i.medicamentoId === id)?.descripcion
      ?? '—';
  }

  const medsFiltrados = $derived.by(() => {
    const q = medBusqueda.trim().toLowerCase();
    if (!q) {
      const recientes = indicacionesHospital.map(i => i.descripcion as string).filter(Boolean);
      const byRecent = [...medsHospital].sort((a, b) => {
        const ia = recientes.indexOf(a.nombre);
        const ib = recientes.indexOf(b.nombre);
        return (ia === -1 ? 999 : ia) - (ib === -1 ? 999 : ib);
      });
      return byRecent.slice(0, 10);
    }
    return medsHospital.filter(m => (m.nombre ?? '').toLowerCase().includes(q)).slice(0, 10);
  });
  let citasPaciente = $state<any[]>([]);
  let altasPaciente = $state<any[]>([]);
  let episodiosPaciente = $state<any[]>([]);
  let episodioAbiertoId = $state<string | null>(null);
  const fisioterapistaActual = $derived(
    autorFisioterapista({ profesional: medicoActual, usuario: session.value?.usuario }),
  );

  type SignoFila = Signo & { origen?: string };

  const signosConTriaje = $derived.by((): SignoFila[] => {
    const base = signos.map(s => ({ ...s, origen: 'Expediente' }));
    if (!esHospital) return base;
    const desdeTriaje: SignoFila[] = triajesPaciente.map(t => ({
      id: `triaje-${t.id}`,
      pacienteId: t.pacienteId,
      ta: t.ta ?? null,
      fc: t.fc ?? null,
      fr: t.fr ?? null,
      temp: t.temp ?? null,
      satO2: t.satO2 ?? null,
      registradoEn: t.fecha,
      origen: 'Triaje',
    }));
    return [...base, ...desdeTriaje].sort((a, b) => {
      const ta = a.registradoEn ? new Date(a.registradoEn).getTime() : 0;
      const tb = b.registradoEn ? new Date(b.registradoEn).getTime() : 0;
      return tb - ta;
    });
  });

  function normalizarCui(raw: string): string {
    return raw.replace(/\D/g, '');
  }

  function cuiValido(cui: string): boolean {
    return cui.length === 13;
  }

  async function cargarExpediente(p: Paciente) {
    if (!p.id) return;
    cargandoDetalle = true;
    error = null;
    try {
      const [n, s, rec, ing, prof, labList, imgList, kdx, cit, alt, ind, tri, meds, eps] = await Promise.all([
        Notas.porPaciente(p.id).catch(() => []),
        Signos.porPaciente(p.id).catch(() => []),
        FarmaciaClinica.recetas(p.id).catch(() => []),
        Ingresos.porPaciente(p.id).catch(() => []),
        resolveMedicoProfesional().catch(() => null),
        Laboratorio.ordenes(undefined, p.id).catch(() => []),
        Imagenologia.ordenes(undefined, p.id).catch(() => []),
        Hospital.kardexPaciente(p.id).catch(() => []),
        Citas.porPaciente(p.id).catch(() => []),
        Hospital.altaPorPaciente(p.id).catch(() => []),
        esHospital ? Hospital.indicaciones(p.id).catch(() => []) : Promise.resolve([]),
        esHospital ? Triajes.porPaciente(p.id).catch(() => []) : Promise.resolve([]),
        esHospital && medsHospital.length === 0 ? Farmacia.meds().catch(() => []) : Promise.resolve(medsHospital),
        Episodios.porPaciente(p.id).catch(() => []),
      ]);
      notas = n;
      rutinaEdit = rutinaMasReciente(n) ?? rutinaVacia();
      signos = s;
      recetasPaciente = rec;
      ingresos = ing;
      medicoActual = prof?.nombre ?? session.value?.usuario ?? '';
      kardex = kdx;
      citasPaciente = cit;
      altasPaciente = alt;
      indicacionesHospital = ind;
      triajesPaciente = tri;
      episodiosPaciente = eps;
      const abierto = eps.find((ep: any) => ep.estado === 'ABIERTO');
      episodioAbiertoId = abierto?.id ?? null;
      if (meds.length) medsHospital = meds;
      labOrdenes = (await Promise.all((labList ?? []).map(async (o: any) => {
        if (!o?.id) return null;
        try { return await Laboratorio.orden(o.id); } catch { return null; }
      }))).filter(Boolean);
      imgOrdenes = (await Promise.all((imgList ?? []).map(async (o: any) => {
        if (!o?.id) return null;
        try { return await Imagenologia.orden(o.id); } catch { return { orden: o, estudios: [] }; }
      }))).filter(Boolean);
      placas = listPlacas(p.id);
      adjuntos = await listAdjuntos(p.id).catch(() => []);
      cargarFicha(p, abierto);
      if (!recetaDiagnostico.trim()) recetaDiagnostico = fichaEdit.diagnostico ?? '';
    } catch (e: any) {
      error = e?.message ?? String(e);
    } finally {
      cargandoDetalle = false;
    }
  }

  async function buscarPorCui(e?: SubmitEvent) {
    e?.preventDefault();
    error = null;
    info = null;
    mostrarAlta = false;
    const cui = normalizarCui(cuiInput);
    if (!cuiValido(cui)) {
      error = 'El CUI debe tener 13 dígitos (módulo 11).';
      paciente = null;
      return;
    }
    buscando = true;
    try {
      const p = await Pacientes.porCui(cui);
      paciente = p;
      cuiInput = cui;
      sessionStorage.setItem(CUI_STORAGE, cui);
      info = `Expediente cargado · ${p.nombres} ${p.apellidos}`;
      await cargarExpediente(p);
    } catch {
      paciente = null;
      mostrarAlta = true;
      nuevoPac = { ...nuevoPac, cui };
      info = 'No hay expediente con ese CUI. Complete los datos para crearlo.';
    } finally {
      buscando = false;
    }
  }

  async function crearExpediente(e: SubmitEvent) {
    e.preventDefault();
    error = null;
    const cui = normalizarCui(nuevoPac.cui ?? cuiInput);
    if (!cuiValido(cui)) { error = 'CUI inválido.'; return; }
    if (!nuevoPac.nombres.trim() || !nuevoPac.apellidos.trim()) {
      error = 'Nombres y apellidos son obligatorios.';
      return;
    }
    try {
      const p = await Pacientes.crear({ ...nuevoPac, cui });
      paciente = p;
      mostrarAlta = false;
      cuiInput = cui;
      sessionStorage.setItem(CUI_STORAGE, cui);
      info = `Expediente creado para CUI ${cui}.`;
      await cargarExpediente(p);
    } catch (err: any) {
      error = err?.message ?? String(err);
    }
  }

  async function guardarRutina(e: SubmitEvent) {
    e.preventDefault();
    if (!paciente?.id) return;
    const ejercicios = rutinaEdit.ejercicios.filter(x => x.nombre.trim());
    if (ejercicios.length === 0) {
      error = 'Agregue al menos un ejercicio con nombre.';
      return;
    }
    error = null;
    try {
      await Notas.crear({
        pacienteId: paciente.id,
        autor: fisioterapistaActual,
        tipo: RUTINA_FISIO_TIPO,
        contenido: serializeRutina({ ...rutinaEdit, ejercicios }),
      });
      info = 'Rutina domiciliaria guardada en el expediente.';
      await cargarExpediente(paciente);
      subTab = 'rutina';
    } catch (err: any) {
      error = err?.message ?? String(err);
    }
  }

  function enviarRutinaWhatsApp() {
    if (!paciente) return;
    const nombre = `${paciente.nombres} ${paciente.apellidos}`.trim();
    const msg = formatRutinaWhatsApp(rutinaEdit, nombre, {
      clinicaNombre: tenantCtx.displayName,
      fisioterapista: fisioterapistaActual,
    });
    const url = whatsappEnlaceRutina(paciente.telefono, msg);
    if (!url) {
      error = 'El paciente no tiene teléfono/WhatsApp registrado.';
      return;
    }
    window.open(url, '_blank', 'noopener,noreferrer');
    info = 'Se abrió WhatsApp con la rutina lista para enviar.';
    error = null;
  }

  function agregarEjercicioRutina() {
    rutinaEdit = { ...rutinaEdit, ejercicios: [...rutinaEdit.ejercicios, EJERCICIO_VACIO()] };
  }

  function quitarEjercicioRutina(idx: number) {
    rutinaEdit = {
      ...rutinaEdit,
      ejercicios: rutinaEdit.ejercicios.filter((_, i) => i !== idx),
    };
  }

  function agregarPlantillaRutina(p: (typeof PLANTILLAS_EJERCICIO_FISIO)[number]) {
    rutinaEdit = { ...rutinaEdit, ejercicios: [...rutinaEdit.ejercicios, { ...p }] };
  }

  async function agregarNota(e: SubmitEvent) {
    e.preventDefault();
    if (!paciente?.id || !notaNueva.contenido.trim()) return;
    error = null;
    try {
      await Notas.crear({
        pacienteId: paciente.id,
        autor: notaNueva.autor || fisioterapistaActual,
        contenido: notaNueva.contenido,
      });
      notaNueva = { autor: '', contenido: '' };
      await cargarExpediente(paciente);
    } catch (err: any) {
      error = err?.message ?? String(err);
    }
  }

  async function guardarTratamientoHospital(e: SubmitEvent) {
    e.preventDefault();
    if (!paciente?.id) return;
    if (!tratamientoNuevo.medicamentoId) { error = 'Seleccione un medicamento del catálogo hospitalario.'; return; }
    if (!tratamientoNuevo.dosis.trim()) { error = 'Indique la dosis.'; return; }
    if (!tratamientoNuevo.horaProgramada) { error = 'Indique la hora de la primera dosis.'; return; }
    guardandoTratamiento = true;
    error = null;
    try {
      const med = medsHospital.find(m => m.id === tratamientoNuevo.medicamentoId);
      const descripcion = tratamientoNuevo.descripcion.trim() || med?.nombre || 'Medicamento';
      const indicacion = await Hospital.crearIndicacion({
        pacienteId: paciente.id,
        medicamentoId: tratamientoNuevo.medicamentoId,
        descripcion,
        dosis: tratamientoNuevo.dosis,
        via: tratamientoNuevo.via,
        frecuencia: tratamientoNuevo.frecuencia,
        indicadaPor: medicoActual || session.value?.usuario || 'Médico',
        activa: true,
      });
      await Hospital.programarAdmin({
        pacienteId: paciente.id,
        medicamentoId: tratamientoNuevo.medicamentoId,
        medicamentoNombre: descripcion,
        indicacionId: indicacion.id,
        dosis: tratamientoNuevo.dosis,
        via: tratamientoNuevo.via,
        horaProgramada: new Date(tratamientoNuevo.horaProgramada).toISOString(),
        estado: 'PENDIENTE',
      });
      tratamientoNuevo = {
        medicamentoId: '',
        descripcion: '',
        dosis: '',
        via: 'IV',
        frecuencia: 'c/8h',
        horaProgramada: '',
      };
      medBusqueda = '';
      medPickerOpen = false;
      info = 'Tratamiento indicado y programado en kárdex de enfermería.';
      await cargarExpediente(paciente);
    } catch (err: any) {
      error = err?.message ?? String(err);
    } finally {
      guardandoTratamiento = false;
    }
  }

  function seleccionarMedicamento(m: Medicamento) {
    if (!m?.id) return;
    tratamientoNuevo.medicamentoId = m.id;
    tratamientoNuevo.descripcion = m.nombre ?? '';
    medBusqueda = m.nombre ?? '';
    medPickerOpen = false;
  }

  async function registrarSigno(e: SubmitEvent) {
    e.preventDefault();
    if (!paciente?.id) return;
    error = null;
    try {
      await Signos.registrar({
        pacienteId: paciente.id,
        ta: signoNuevo.ta || null,
        fc: signoNuevo.fc != null ? Number(signoNuevo.fc) : null,
        fr: signoNuevo.fr != null ? Number(signoNuevo.fr) : null,
        temp: signoNuevo.temp != null ? Number(signoNuevo.temp) : null,
        satO2: signoNuevo.satO2 != null ? Number(signoNuevo.satO2) : null,
        peso: signoNuevo.peso != null ? Number(signoNuevo.peso) : null,
        talla: signoNuevo.talla != null ? Number(signoNuevo.talla) : null,
      });
      signoNuevo = { pacienteId: '', ta: '', fc: null, fr: null, temp: null, satO2: null, peso: null, talla: null };
      await cargarExpediente(paciente);
    } catch (err: any) {
      error = err?.message ?? String(err);
    }
  }

  function onArchivoPlaca(e: Event) {
    const input = e.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    placaNueva.archivoNombre = file.name;
    const reader = new FileReader();
    reader.onload = () => {
      placaNueva.imagenUrl = typeof reader.result === 'string' ? reader.result : '';
    };
    reader.readAsDataURL(file);
  }

  function guardarPlaca(e: SubmitEvent) {
    e.preventDefault();
    if (!paciente?.id) return;
    if (!placaNueva.dientes.trim()) { error = 'Indique el diente o región.'; return; }
    addPlaca({
      pacienteId: paciente.id,
      cui: paciente.cui,
      fecha: new Date().toISOString().slice(0, 10),
      tipo: placaNueva.tipo,
      dientes: placaNueva.dientes,
      nota: placaNueva.nota,
      medico: placaNueva.medico || session.value?.usuario || 'Odontólogo',
      archivoNombre: placaNueva.archivoNombre || undefined,
      imagenUrl: placaNueva.imagenUrl || undefined,
    });
    placaNueva = { tipo: 'PERIAPICAL', dientes: '', nota: '', medico: '', archivoNombre: '', imagenUrl: '' };
    placas = listPlacas(paciente.id);
    info = 'Placa registrada en el expediente clínico.';
    error = null;
  }

  function eliminarPlaca(id: string) {
    if (!paciente?.id) return;
    removePlaca(id);
    placas = listPlacas(paciente.id);
  }

  function onArchivoAdjunto(ev: Event) {
    const input = ev.target as HTMLInputElement;
    const file = input.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => {
      adjuntoNuevo.archivoNombre = file.name;
      adjuntoNuevo.dataUrl = String(reader.result ?? '');
      if (!adjuntoNuevo.titulo) adjuntoNuevo.titulo = file.name.replace(/\.[^.]+$/, '');
    };
    reader.readAsDataURL(file);
  }

  async function guardarAdjunto(e: SubmitEvent) {
    e.preventDefault();
    if (!paciente?.id) return;
    if (!adjuntoNuevo.titulo.trim()) { error = 'Indique el título del estudio.'; return; }
    try {
      await addAdjunto({
        pacienteId: paciente.id,
        fecha: new Date().toISOString().slice(0, 10),
        titulo: adjuntoNuevo.titulo.trim(),
        origen: adjuntoNuevo.origen,
        tipo: adjuntoNuevo.tipo,
        nota: adjuntoNuevo.nota,
        archivoNombre: adjuntoNuevo.archivoNombre,
        dataUrl: adjuntoNuevo.dataUrl,
      });
      adjuntoNuevo = { titulo: '', origen: 'EXTERNO', tipo: 'REPORTE', nota: '', archivoNombre: '', dataUrl: '' };
      adjuntos = await listAdjuntos(paciente.id);
      info = 'Estudio adjunto al expediente.';
      error = null;
    } catch (err: any) { error = err?.message ?? String(err); }
  }

  async function quitarAdjunto(id: string) {
    if (!paciente?.id) return;
    try {
      await removeAdjunto(id);
      adjuntos = await listAdjuntos(paciente.id);
    } catch (err: any) { error = err?.message ?? String(err); }
  }

  function ahoraLocal() {
    const d = new Date();
    const p = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`;
  }

  async function guardarPostop(e: SubmitEvent) {
    e.preventDefault();
    if (!paciente?.id) return;
    const evo = postop.evolucion.trim();
    const med = postop.medicamento.trim();
    if (!evo && !med) {
      error = 'Indique la evolución postoperatoria o el medicamento.';
      return;
    }
    const contenido = [
      `Fecha/hora: ${postop.fechaHora || new Date().toLocaleString('es-GT')}`,
      evo ? `Evolución postoperatoria: ${evo}` : '',
      med ? `Medicamento postoperatorio: ${med}` : '',
    ].filter(Boolean).join('\n');
    try {
      await Notas.crear({
        pacienteId: paciente.id,
        autor: session.value?.usuario || medicoActual || 'Médico',
        contenido,
        tipo: 'POSTOP',
      });
      postop = { fechaHora: ahoraLocal(), evolucion: '', medicamento: '' };
      info = 'Nota postquirúrgica registrada.';
      error = null;
      await cargarExpediente(paciente);
    } catch (err: any) { error = err?.message ?? String(err); }
  }

  function abrirDemoCui(cui: string) {
    cuiInput = cui;
    void buscarPorCui();
  }

  async function abrirPorPaciente(id: string, picked?: Paciente) {
    if (!id) {
      pickerPacienteId = '';
      try { sessionStorage.removeItem(CUI_STORAGE); } catch { /* ignore */ }
      cuiInput = '';
      paciente = null;
      mostrarAlta = false;
      info = null;
      error = null;
      return;
    }
    pickerPacienteId = id;
    error = null;
    info = null;
    mostrarAlta = false;
    buscando = true;
    try {
      const p = picked?.id === id ? picked : await Pacientes.uno(id);
      paciente = p;
      cuiInput = p.cui ?? '';
      if (p.cui) sessionStorage.setItem(CUI_STORAGE, normalizarCui(p.cui));
      info = `Expediente cargado · ${p.nombres} ${p.apellidos}`;
      await cargarExpediente(p);
    } catch (err: any) {
      error = err?.message ?? String(err);
      paciente = null;
    } finally {
      buscando = false;
    }
  }

  const ingresoActivo = $derived(
    ingresos.find(i => i.estado === 'ACTIVO' && !i.egresadoEn)
      ?? ingresos.find(i => !i.egresadoEn)
      ?? ingresos[0]
      ?? null,
  );

  function agregarItemReceta() {
    recetaItems.push({ descripcion: '', cantidad: 1, indicaciones: '' });
  }
  function quitarItemReceta(i: number) {
    recetaItems.splice(i, 1);
  }

  async function emitirReceta(e: SubmitEvent) {
    e.preventDefault();
    if (!paciente?.id) return;
    if (recetaItems.length === 0) { error = 'Agrega al menos un medicamento.'; return; }
    error = null;
    try {
      const medicoId = await resolveMedicoId();
      await FarmaciaClinica.crearReceta({
        pacienteId: paciente.id,
        medicoId: medicoId ?? null,
        diagnostico: recetaDiagnostico || null,
        items: recetaItems.map(it => ({ ...it, cantidad: Number(it.cantidad) })),
      });
      recetaDiagnostico = '';
      recetaItems = [];
      info = 'Receta emitida. La farmacia puede cargarla en el POS o imprimirla aquí.';
      await cargarExpediente(paciente);
    } catch (err: any) {
      error = err?.message ?? String(err);
    }
  }

  function abrirHtmlReceta(html: string) {
    abrirOImprimirHtml(html);
  }

  async function htmlDeReceta(r: Receta): Promise<string> {
    if (!r.id || !paciente) throw new Error('No hay receta o paciente.');
    const nombre = `${paciente.nombres} ${paciente.apellidos}`.trim();
    return FarmaciaClinica.recetaImprimir(r.id, nombre, paciente.cui ?? '');
  }

  async function imprimirReceta(r: Receta) {
    error = null;
    try {
      imprimirHtml(await htmlDeReceta(r));
    } catch (err: any) {
      error = err?.message ?? String(err);
    }
  }

  async function verReceta(r: Receta) {
    error = null;
    try {
      abrirHtmlReceta(await htmlDeReceta(r));
    } catch (err: any) {
      error = err?.message ?? String(err);
    }
  }

  async function enviarRecetaWhatsApp(r: Receta) {
    if (!paciente) return;
    const tel = telefonoWhatsAppGt(paciente.telefono);
    if (!tel) { error = 'El paciente no tiene teléfono/WhatsApp registrado.'; return; }
    error = null;
    let receta = r;
    try {
      if (r.id && (!r.items || r.items.length === 0)) {
        receta = await FarmaciaClinica.receta(r.id);
      }
    } catch { /* usamos la receta de la lista */ }
    const msg = mensajeRecetaWhatsApp({
      receta,
      pacienteNombre: `${paciente.nombres} ${paciente.apellidos}`.trim(),
      hospitalNombre: tenantCtx.displayName || 'Hospital',
      medicoNombre: medicoActual,
      tenantId: session.value?.tenantId ?? '',
    });
    window.open(`https://wa.me/${tel}?text=${encodeURIComponent(msg)}`, '_blank');
  }

  function esc(v: any): string {
    return String(v ?? '')
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  function imprimirExpediente() {
    if (!paciente) return;
    const nombre = `${paciente.nombres} ${paciente.apellidos}`.trim();
    const ahora = new Date();
    const fecha = ahora.toLocaleString('es-GT');
    const clinica = tenantCtx.displayName ?? 'Toscana';
    const usuario = session.value?.usuario ?? '';
    const cui = paciente.cui ?? normalizarCui(cuiInput);

    let edad = '—';
    if (paciente.fechaNacimiento) {
      const nac = new Date(paciente.fechaNacimiento);
      const diff = ahora.getTime() - nac.getTime();
      const anios = Math.floor(diff / (1000 * 60 * 60 * 24 * 365.25));
      edad = `${anios} años`;
    }

    const filasSignos = signos.map(s => `<tr>
      <td>${esc(s.registradoEn ? new Date(s.registradoEn).toLocaleString('es-GT') : '—')}</td>
      <td>${esc(s.ta)}</td><td>${esc(s.fc)}</td><td>${esc(s.fr)}</td>
      <td>${esc(s.temp)}</td><td>${esc(s.satO2)}</td><td>${esc(s.peso)}</td><td>${esc(s.talla)}</td>
    </tr>`).join('');

    const filasRecetas = recetasPaciente.map(r => {
      const medicamentos = (r.items ?? []).map(it => {
        const ind = it.indicaciones ? ` — ${it.indicaciones}` : '';
        return `${it.descripcion} (${it.cantidad})${ind}`;
      }).join('; ');
      return `<tr>
        <td>${esc(r.fecha?.slice(0, 10))}</td>
        <td>${esc(r.diagnostico)}</td>
        <td>${esc(medicamentos)}</td>
        <td>${esc(r.estado)}</td>
      </tr>`;
    }).join('');

    const notasHtml = notasClinicas.map(n => `<div class="nota">
      <div class="nota-meta">${esc(n.autor)} · ${esc(n.registradaEn ? new Date(n.registradaEn).toLocaleString('es-GT') : '')}</div>
      <p>${esc(n.contenido)}</p>
    </div>`).join('');

    const filasPlacas = placas.map(p => `<tr>
      <td>${esc(p.dientes)}</td><td>${esc(p.tipo)}</td><td>${esc(p.fecha)}</td>
      <td>${esc(p.medico)}</td><td>${esc(p.nota)}</td>
    </tr>`).join('');

    const filasIngresos = ingresos.map(i => `<tr>
      <td>${esc(i.ingresadoEn ? new Date(i.ingresadoEn).toLocaleString('es-GT') : '—')}</td>
      <td>${esc(i.egresadoEn ? new Date(i.egresadoEn).toLocaleString('es-GT') : 'En curso')}</td>
      <td>${esc(i.diagnosticoIngreso)}</td>
      <td>${esc(i.estado)}</td>
    </tr>`).join('');

    const filasKardex = kardex.map(k => `<tr>
      <td>${esc(nombreMedicamento(k))}</td>
      <td>${esc(k.dosis)}</td>
      <td>${esc(k.via)}</td>
      <td>${esc(k.horaProgramada ? new Date(k.horaProgramada).toLocaleString('es-GT') : '—')}</td>
      <td>${esc(k.estado)}</td>
    </tr>`).join('');

    const filasCitas = citasPaciente.map(c => `<tr>
      <td>${esc(c.fechaHora ? new Date(c.fechaHora).toLocaleString('es-GT') : '—')}</td>
      <td>${esc(c.motivo)}</td>
      <td>${esc(c.medico)}</td>
      <td>${esc(c.consultorio)}</td>
      <td>${esc(c.estado)}</td>
    </tr>`).join('');

    const filasLab = labOrdenes.map(det => {
      const o = det.orden ?? {};
      const examenes = (det.examenes ?? []).map((ex: any) => `<tr>
        <td>${esc(ex.examenNombre ?? ex.examenCodigo)}</td>
        <td>${esc(ex.valor ?? '—')} ${esc(ex.unidad ?? '')}</td>
        <td>${esc(ex.valorRefTexto ?? (ex.valorRefMin != null && ex.valorRefMax != null ? `${ex.valorRefMin} - ${ex.valorRefMax}` : '—'))}</td>
        <td>${esc(ex.flag ?? '')}</td>
      </tr>`).join('');
      return `<div class="lab-block"><div class="lab-head">${esc(o.numero ?? '')} · ${esc(o.medicoSolicitante ?? '')} · ${esc(estadoLabLabel(o.estado))}</div>
        ${examenes ? `<table><thead><tr><th>Examen</th><th>Resultado</th><th>Ref.</th><th>Flag</th></tr></thead><tbody>${examenes}</tbody></table>` : '<p class="vacio">Sin resultados.</p>'}
      </div>`;
    }).join('');

    const filasImg = imgOrdenes.map(det => {
      const o = det.orden ?? det;
      const estudios = (det.estudios ?? []).map((e: any) => e.estudioNombre).filter(Boolean).join(', ');
      const txt = [
        estudios ? `<p><b>Estudios:</b> ${esc(estudios)}</p>` : '',
        o.indicacionClinica ? `<p><b>Indicación:</b> ${esc(o.indicacionClinica)}</p>` : '',
        o.hallazgos ? `<p><b>Hallazgos:</b> ${esc(o.hallazgos)}</p>` : '',
        o.impresion ? `<p><b>Impresión:</b> ${esc(o.impresion)}</p>` : '',
        o.radiologo ? `<p class="vacio">Informado por ${esc(o.radiologo)}</p>` : '',
      ].join('');
      return `<div class="lab-block"><div class="lab-head">${esc(o.numero ?? '')} · ${esc(o.medicoSolicitante ?? '')} · ${esc(estadoImagenLabel(o.estado))}</div>${txt}</div>`;
    }).join('');

    const filasAltas = altasPaciente.map(a => {
      const txt = [
        a.diagnosticoEgreso ? `<p><b>Diagnóstico de egreso:</b> ${esc(a.diagnosticoEgreso)}</p>` : '',
        a.indicaciones ? `<p><b>Indicaciones:</b> ${esc(a.indicaciones)}</p>` : '',
        a.recetas ? `<p><b>Recetas:</b> ${esc(a.recetas)}</p>` : '',
        a.cuidadosEnCasa ? `<p><b>Cuidados en casa:</b> ${esc(a.cuidadosEnCasa)}</p>` : '',
        a.proximaCita ? `<p><b>Próxima cita:</b> ${esc(a.proximaCita)}</p>` : '',
      ].join('');
      return `<div class="lab-block"><div class="lab-head">${esc(a.fechaAlta ? new Date(a.fechaAlta).toLocaleDateString('es-GT') : '—')} · Condición ${esc(a.condicionEgreso ?? '—')} · ${esc(a.firmadaPor ?? '')}</div>${txt}</div>`;
    }).join('');

    const seccion = (titulo: string, cuerpo: string) =>
      `<h2>${titulo}</h2>${cuerpo || '<p class="vacio">Sin registro.</p>'}`;

    const html = `<!DOCTYPE html><html lang="es"><head><meta charset="utf-8">
<title>Expediente ${esc(cui)}</title>
<style>
  * { box-sizing: border-box; }
  body { font-family: -apple-system, "Segoe UI", Roboto, Arial, sans-serif; color: #111; margin: 28px; font-size: 13px; }
  .membrete { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 3px solid #15803d; padding-bottom: 10px; margin-bottom: 4px; }
  .membrete .logo { font-weight: 800; color: #15803d; font-size: 16px; }
  .membrete .lugar { color: #555; font-size: 11px; }
  .membrete .folio { color: #666; font-size: 11px; text-align: right; }
  h1 { font-size: 20px; text-align: center; letter-spacing: 1px; margin: 10px 0 2px; color: #111; }
  .sub { text-align: center; color: #555; margin: 0 0 16px; }
  .dato { display: grid; grid-template-columns: 150px 1fr 150px 1fr; gap: 3px 12px; margin: 0 0 14px; font-size: 12px; border: 1px solid #d1d5db; padding: 10px 12px; }
  .dato b { color: #333; font-weight: 600; }
  h2 { font-size: 13px; color: #166534; border-bottom: 1px solid #16a34a; padding-bottom: 4px; margin: 20px 0 8px; text-transform: uppercase; letter-spacing: .04em; }
  table { width: 100%; border-collapse: collapse; margin: 6px 0; }
  th, td { border: 1px solid #d1d5db; padding: 6px 8px; text-align: left; vertical-align: top; font-size: 12px; }
  th { background: #f0fdf4; font-size: 10px; text-transform: uppercase; letter-spacing: .03em; color: #555; }
  .nota { border-left: 4px solid #16a34a; padding: 6px 10px; margin: 8px 0; background: #fafafa; }
  .nota-meta { font-size: 11px; color: #666; margin-bottom: 4px; }
  .nota p { margin: 0; white-space: pre-wrap; }
  .texto { border: 1px solid #e5e7eb; padding: 8px 10px; min-height: 20px; white-space: pre-wrap; font-size: 12px; }
  .vacio { color: #999; font-style: italic; }
  .lab-block { border: 1px solid #e5e7eb; border-radius: 8px; padding: 8px 10px; margin: 6px 0; }
  .lab-head { font-weight: 700; color: #166534; font-size: 11px; margin-bottom: 4px; }
  .lab-block p { margin: 3px 0; font-size: 12px; }
  .firma { margin-top: 40px; display: flex; justify-content: space-between; gap: 40px; }
  .firma .bloque { flex: 1; text-align: center; font-size: 12px; color: #333; }
  .firma .linea { border-top: 1px solid #333; padding-top: 6px; margin-top: 50px; }
  .footer { margin-top: 26px; font-size: 10px; color: #888; border-top: 1px solid #d1d5db; padding-top: 8px; text-align: center; }
  @media print { body { margin: 12mm; } h2 { break-after: avoid; } .nota, table, .dato { break-inside: avoid; } }
</style></head>
<body onload="setTimeout(function(){window.print()},300)">
  <div class="membrete">
    <div><div class="logo">${esc(clinica)}</div><div class="lugar">Hospitales y clínicas privadas</div></div>
    <div class="folio">Expediente ${esc(cui)}<br>${esc(fecha)}</div>
  </div>
  <h1>EXPEDIENTE CLÍNICO</h1>
  <div class="sub">${esc(nombre)}</div>

  ${seccion('1. Identificación del paciente', `<div class="dato">
    <b>Nombre</b><span>${esc(nombre)}</span>
    <b>CUI</b><span>${esc(cui)}</span>
    <b>Sexo</b><span>${esc(paciente.sexo)}</span>
    <b>Edad</b><span>${esc(edad)}</span>
    <b>Fecha de nacimiento</b><span>${esc(paciente.fechaNacimiento)}</span>
    <b>Teléfono</b><span>${esc(paciente.telefono)}</span>
    <b>Dirección</b><span>${esc(paciente.direccion)}</span>
    <b>Correo</b><span>${esc(paciente.email)}</span>
  </div>`)}

  ${seccion('2. Motivo de consulta', paciente.motivoConsulta ? `<div class="texto">${esc(paciente.motivoConsulta)}</div>` : '')}

  ${seccion('3. Antecedentes', `
    <b>Personales:</b> ${paciente.antecedentesPersonales ? `<div class="texto">${esc(paciente.antecedentesPersonales)}</div>` : '<span class="vacio">—</span>'}
    <br><b>Familiares:</b> ${paciente.antecedentesFamiliares ? `<div class="texto">${esc(paciente.antecedentesFamiliares)}</div>` : '<span class="vacio">—</span>'}
    <br><b>Quirúrgicos:</b> ${paciente.antecedentesQuirurgicos ? `<div class="texto">${esc(paciente.antecedentesQuirurgicos)}</div>` : '<span class="vacio">—</span>'}
    <br><b>Alergias:</b> ${paciente.alergias ? `<div class="texto">${esc(paciente.alergias)}</div>` : '<span class="vacio">—</span>'}
  `)}

  ${seccion('4. Signos vitales', signos.length
    ? `<table><thead><tr><th>Fecha</th><th>TA</th><th>FC</th><th>FR</th><th>Temp</th><th>Sat</th><th>Peso</th><th>Talla</th></tr></thead><tbody>${filasSignos}</tbody></table>`
    : '')}

  ${seccion('5. Historial de hospitalizaciones', ingresos.length
    ? `<table><thead><tr><th>Fecha de ingreso</th><th>Fecha de egreso</th><th>Diagnóstico de ingreso</th><th>Estado</th></tr></thead><tbody>${filasIngresos}</tbody></table>`
    : '')}

  ${altasPaciente.length ? seccion('6. Altas hospitalarias', filasAltas) : ''}

  ${labOrdenes.length ? seccion('7. Resultados de laboratorio', filasLab) : ''}

  ${imgOrdenes.length ? seccion('8. Imagenología', filasImg) : ''}

  ${kardex.length ? seccion('9. Medicamentos (kárdex)', `<table><thead><tr><th>Medicamento</th><th>Dosis</th><th>Vía</th><th>Programada</th><th>Estado</th></tr></thead><tbody>${filasKardex}</tbody></table>`) : ''}

  ${citasPaciente.length ? seccion('10. Citas', `<table><thead><tr><th>Fecha</th><th>Motivo</th><th>Médico</th><th>Consultorio</th><th>Estado</th></tr></thead><tbody>${filasCitas}</tbody></table>`) : ''}

  ${placas.length ? seccion('11. Exploraciones complementarias', `<table><thead><tr><th>Diente(s)</th><th>Tipo</th><th>Fecha</th><th>Odontólogo</th><th>Nota</th></tr></thead><tbody>${filasPlacas}</tbody></table>`) : ''}

  ${seccion('12. Diagnóstico', paciente.diagnostico ? `<div class="texto">${esc(paciente.diagnostico)}</div>` : '')}

  ${seccion('13. Tratamiento / recetas', recetasPaciente.length
    ? `<table><thead><tr><th>Fecha</th><th>Diagnóstico</th><th>Medicamentos</th><th>Estado</th></tr></thead><tbody>${filasRecetas}</tbody></table>`
    : '')}

  ${seccion('14. Notas de evolución', notasHtml)}

  <div class="firma">
    <div class="bloque"><div class="linea">Firma y sello del médico</div></div>
    <div class="bloque"><div class="linea">${esc(usuario)}</div></div>
  </div>

  <div class="footer">Documento confidencial · Generado el ${esc(fecha)} por ${esc(usuario)}</div>
</body></html>`;

    const w = window.open('', '_blank');
    if (w) {
      w.document.open();
      w.document.write(html);
      w.document.close();
    } else {
      error = 'El navegador bloqueó la ventana de impresión.';
    }
  }

</script>

<section class="expediente">
  <header>
    <h2>Expediente clínico</h2>
    <p class="hint">
      Cada paciente tiene un expediente único identificado por <strong>CUI</strong> (13 dígitos).
      {#if esHospital}
        Aplica a hospitalizados, urgencias y consulta externa: una sola ficha longitudinal por persona.
      {:else if esDental}
        En odontología, las placas y radiografías tomadas en consultorio se archivan aquí.
      {:else if esFisio}
        Registre evaluaciones por objetivo (dolor, edema, sensibilidad, fuerza, reflejos, ROM), evolución por sesión y rutina domiciliaria. Las plantillas son editables por su clínica.
      {/if}
    </p>
  </header>

  {#if esDemo && demoHint}
    <div class="demo-banner" role="note">
      <strong>Demo expediente clínico</strong>
      <span>
        Busque CUI
        <button type="button" class="cui-link" onclick={() => abrirDemoCui(demoHint.cui)}>
          {demoHint.cui}
        </button>
        · {demoHint.nombre}
      </span>
      <span class="demo-more">
        {#if orgTipo === 'clinica-medica'}
          También: {DEMO_CUIS.secundario} (Sofía Martínez)
        {:else if orgTipo === 'clinica-odontologica'}
          CUI dental: {DEMO_CUIS.dental} · placas en pestaña RX
        {:else if esFisio && esDemo}
          Abra expediente por CUI · pestaña <strong>Rutina domiciliaria</strong> · envío WhatsApp con marca de su clínica
        {/if}
      </span>
    </div>
  {/if}

  <div class="buscar-bar">
    <label class="cui-field grow">
      <span>Buscar por nombre</span>
      <PacientePicker
        bind:value={pickerPacienteId}
        onPick={(id, p) => { void abrirPorPaciente(id, p); }}
        placeholder="Nombre, apellido o CUI (mín. 2 letras)…"
      />
    </label>
    <form class="cui-bar" onsubmit={buscarPorCui}>
      <label class="cui-field">
        <span>CUI del paciente</span>
        <input
          inputmode="numeric"
          maxlength="17"
          placeholder="Ej. 1234 56789 0101"
          bind:value={cuiInput}
          disabled={buscando}
        />
      </label>
      <button type="submit" disabled={buscando}>{buscando ? 'Buscando…' : 'Abrir expediente'}</button>
    </form>
  </div>

  {#if error}<div class="err">{error}</div>{/if}
  {#if info}<div class="ok">{info}</div>{/if}

  {#if mostrarAlta}
    <div class="alta-card">
      <h3>Crear expediente clínico</h3>
      <p class="hint">No existe registro con CUI <strong>{normalizarCui(cuiInput)}</strong>. Complete los datos mínimos.</p>
      <form class="form-grid" onsubmit={crearExpediente}>
        <label>Nombres <input bind:value={nuevoPac.nombres} required /></label>
        <label>Apellidos <input bind:value={nuevoPac.apellidos} required /></label>
        <label>CUI <input value={normalizarCui(nuevoPac.cui ?? cuiInput)} readonly class="readonly" /></label>
        <label>Sexo
          <select bind:value={nuevoPac.sexo}>
            <option value="M">Masculino</option>
            <option value="F">Femenino</option>
            <option value="O">Otro</option>
          </select>
        </label>
        <label>Fecha nacimiento <input type="date" bind:value={nuevoPac.fechaNacimiento} /></label>
        <label>Teléfono <input bind:value={nuevoPac.telefono} /></label>
        <button type="submit" class="primary">Crear expediente</button>
      </form>
    </div>
  {/if}

  {#if paciente}
    <div class="paciente-card">
      <div class="paciente-head">
        <div>
          <strong>{paciente.nombres} {paciente.apellidos}</strong>
          <span class="cui-badge">CUI {paciente.cui ?? normalizarCui(cuiInput)}</span>
          <div class="paciente-meta">
            {#if paciente.sexo}<span>Sexo: {paciente.sexo}</span>{/if}
            {#if paciente.fechaNacimiento}<span>Nac.: {paciente.fechaNacimiento}</span>{/if}
            <span>Edad: {edadDesdeNacimiento(paciente.fechaNacimiento)}</span>
            {#if paciente.telefono}<span>{paciente.telefono}</span>{/if}
            {#if ingresos.length > 0 && ingresoActivo?.ingresadoEn}
              <span class="admision">Admisión: {new Date(ingresoActivo.ingresadoEn).toLocaleDateString('es-GT')}</span>
            {/if}
          </div>
        </div>
        <button type="button" class="print-btn" onclick={imprimirExpediente}>Imprimir expediente</button>
      </div>

      <nav class="subtabs">
        <button type="button" class:active={subTab === 'resumen'} onclick={() => subTab = 'resumen'}>Resumen</button>
        <button type="button" class:active={subTab === 'ficha'} onclick={() => subTab = 'ficha'}>Ficha clínica</button>
        <button type="button" class:active={subTab === 'evolucion'} onclick={() => subTab = 'evolucion'}>Evolución</button>
        <button type="button" class:active={subTab === 'signos'} onclick={() => subTab = 'signos'}>
          {esFisio ? `Evaluaciones (${evaluacionesFisio.length})` : 'Signos'}
        </button>
        <button type="button" class:active={subTab === 'diagnostico'} onclick={() => subTab = 'diagnostico'}>
          Diagnóstico ({labOrdenes.length + imgOrdenes.length})
        </button>
        <button type="button" class:active={subTab === 'medicamentos'} onclick={() => subTab = 'medicamentos'}>
          Medicamentos ({kardex.length})
        </button>
        {#if esFisio}
          <button type="button" class:active={subTab === 'rutina'} onclick={() => subTab = 'rutina'}>
            Rutina domiciliaria ({rutinaEdit.ejercicios.filter(e => e.nombre.trim()).length})
          </button>
        {/if}
        {#if !esFisio}
          <button type="button" class:active={subTab === 'receta'} onclick={() => subTab = 'receta'}>
            Receta ℞ ({recetasPaciente.length})
          </button>
        {/if}
        <button type="button" class:active={subTab === 'citas'} onclick={() => subTab = 'citas'}>
          Citas ({citasPaciente.length})
        </button>
        <button type="button" class:active={subTab === 'altas'} onclick={() => subTab = 'altas'}>
          Altas ({altasPaciente.length})
        </button>
        <button type="button" class:active={subTab === 'adjuntos'} onclick={() => subTab = 'adjuntos'}>
          Estudios adjuntos ({adjuntos.length})
        </button>
        {#if esHospital}
          <button type="button" class:active={subTab === 'postop'} onclick={() => subTab = 'postop'}>
            Postoperatorio
          </button>
        {/if}
        {#if esDental}
          <button type="button" class:active={subTab === 'placas'} onclick={() => subTab = 'placas'}>
            Placas / RX ({placas.length})
          </button>
        {/if}
      </nav>

      {#if cargandoDetalle}
        <p class="hint">Cargando expediente…</p>
      {:else if subTab === 'resumen'}
        <div class="resumen-grid">
          <div class="stat"><span>Notas clínicas</span><strong>{notasClinicas.length}</strong></div>
          <div class="stat"><span>{esFisio ? 'Evaluaciones' : 'Signos vitales'}</span><strong>{esFisio ? evaluacionesFisio.length : signos.length}</strong></div>
          {#if esFisio}
            <div class="stat"><span>Ejercicios en rutina</span><strong>{rutinaEdit.ejercicios.filter(e => e.nombre.trim()).length}</strong></div>
          {:else}
            <div class="stat"><span>Recetas</span><strong>{recetasPaciente.length}</strong></div>
          {/if}
          {#if esDental}
            <div class="stat"><span>Placas / radiografías</span><strong>{placas.length}</strong></div>
          {/if}
          <div class="stat wide">
            <span>Última evolución</span>
            <strong>{notasClinicas[0]?.registradaEn ? new Date(notasClinicas[0].registradaEn!).toLocaleString('es-GT') : '—'}</strong>
          </div>
        </div>
        {#if notasClinicas[0]}
          <div class="preview-nota">
            <small>{notasClinicas[0].autor} · {notasClinicas[0].registradaEn ? new Date(notasClinicas[0].registradaEn).toLocaleString('es-GT') : ''}</small>
            <p>{notasClinicas[0].contenido}</p>
          </div>
        {/if}

        <h4 class="sub-sec">Historial de ingresos hospitalarios</h4>
        {#if ingresos.length > 0}
          <table>
            <thead><tr><th>Fecha de ingreso</th><th>Fecha de egreso</th><th>Diagnóstico de ingreso</th><th>Estado</th></tr></thead>
            <tbody>
              {#each ingresos as ing (ing.id)}
                <tr>
                  <td>{ing.ingresadoEn ? new Date(ing.ingresadoEn).toLocaleString('es-GT') : '—'}</td>
                  <td>{ing.egresadoEn ? new Date(ing.egresadoEn).toLocaleString('es-GT') : 'En curso'}</td>
                  <td>{ing.diagnosticoIngreso ?? '—'}</td>
                  <td>{ing.estado ?? '—'}</td>
                </tr>
              {/each}
            </tbody>
          </table>
        {:else}
          <p class="hint">Sin ingresos hospitalarios registrados.</p>
        {/if}
      {:else if subTab === 'ficha'}
        <ConsultaIaPanel onAplicar={aplicarBorradorIa} />
        <form class="ficha-form" onsubmit={guardarFicha}>
          <p class="hint">Antecedentes y motivo de consulta. Use el micrófono para dictar en cada campo.</p>
          <div class="ficha-cols">
            <label class="field"><span>APP — Antecedentes personales</span>
              <div class="dict-row">
                <textarea rows="3" bind:value={fichaEdit.antecedentesPersonales} placeholder="Enfermedades crónicas…"></textarea>
                {#if SpeechRecognition}<button type="button" class="mic" class:on={dictandoCampo === 'antecedentesPersonales'} onclick={() => toggleDictadoFicha('antecedentesPersonales')}>{dictandoCampo === 'antecedentesPersonales' ? '⏹' : '🎤'}</button>{/if}
              </div>
            </label>
            <label class="field"><span>APF — Antecedentes familiares</span>
              <div class="dict-row">
                <textarea rows="3" bind:value={fichaEdit.antecedentesFamiliares} placeholder="Enfermedades hereditarias…"></textarea>
                {#if SpeechRecognition}<button type="button" class="mic" class:on={dictandoCampo === 'antecedentesFamiliares'} onclick={() => toggleDictadoFicha('antecedentesFamiliares')}>{dictandoCampo === 'antecedentesFamiliares' ? '⏹' : '🎤'}</button>{/if}
              </div>
            </label>
          </div>
          <div class="ficha-cols">
            <label class="field"><span>Hábitos tóxicos</span>
              <div class="dict-row">
                <textarea rows="2" bind:value={fichaEdit.habitosToxicos} placeholder="Tabaco, alcohol, drogas…"></textarea>
                {#if SpeechRecognition}<button type="button" class="mic" class:on={dictandoCampo === 'habitosToxicos'} onclick={() => toggleDictadoFicha('habitosToxicos')}>{dictandoCampo === 'habitosToxicos' ? '⏹' : '🎤'}</button>{/if}
              </div>
            </label>
            <label class="field"><span>Alergias</span>
              <div class="dict-row">
                <textarea rows="2" bind:value={fichaEdit.alergias} placeholder="Medicamentos, alimentos…"></textarea>
                {#if SpeechRecognition}<button type="button" class="mic" class:on={dictandoCampo === 'alergias'} onclick={() => toggleDictadoFicha('alergias')}>{dictandoCampo === 'alergias' ? '⏹' : '🎤'}</button>{/if}
              </div>
            </label>
          </div>
          <label class="field full"><span>Antecedentes quirúrgicos</span>
            <div class="dict-row">
              <textarea rows="2" bind:value={fichaEdit.antecedentesQuirurgicos} placeholder="Cirugías previas…"></textarea>
              {#if SpeechRecognition}<button type="button" class="mic" class:on={dictandoCampo === 'antecedentesQuirurgicos'} onclick={() => toggleDictadoFicha('antecedentesQuirurgicos')}>{dictandoCampo === 'antecedentesQuirurgicos' ? '⏹' : '🎤'}</button>{/if}
            </div>
          </label>
          <label class="field full"><span>Motivo de consulta</span>
            <div class="dict-row">
              <textarea rows="2" bind:value={fichaEdit.motivoConsulta} placeholder="Por qué acude hoy"></textarea>
              {#if SpeechRecognition}<button type="button" class="mic" class:on={dictandoCampo === 'motivoConsulta'} onclick={() => toggleDictadoFicha('motivoConsulta')}>{dictandoCampo === 'motivoConsulta' ? '⏹' : '🎤'}</button>{/if}
            </div>
          </label>
          <label class="field full"><span>HEA — Historia de la enfermedad actual</span>
            <div class="dict-row">
              <textarea rows="4" bind:value={fichaEdit.historiaEnfermedadActual} placeholder="Evolución del cuadro actual…"></textarea>
              {#if SpeechRecognition}<button type="button" class="mic" class:on={dictandoCampo === 'historiaEnfermedadActual'} onclick={() => toggleDictadoFicha('historiaEnfermedadActual')}>{dictandoCampo === 'historiaEnfermedadActual' ? '⏹' : '🎤'}</button>{/if}
            </div>
          </label>
          <label class="field full"><span>Diagnóstico</span>
            <div class="dict-row">
              <textarea rows="2" bind:value={fichaEdit.diagnostico} placeholder="Diagnóstico presuntivo o definitivo"></textarea>
              {#if SpeechRecognition}<button type="button" class="mic" class:on={dictandoCampo === 'diagnostico'} onclick={() => toggleDictadoFicha('diagnostico')}>{dictandoCampo === 'diagnostico' ? '⏹' : '🎤'}</button>{/if}
            </div>
          </label>
          <div class="ficha-actions">
            <button type="submit" class="primary" disabled={guardandoFicha}>{guardandoFicha ? 'Guardando…' : 'Guardar ficha clínica'}</button>
          </div>
        </form>
      {:else if subTab === 'evolucion'}
        <form class="form-inline" onsubmit={agregarNota}>
          <input placeholder={esFisio ? 'Fisioterapista (opcional)' : 'Autor (opcional)'} bind:value={notaNueva.autor} />
          <textarea rows="3" placeholder={esFisio ? NOTA_FISIO_PLACEHOLDER : 'Nota de evolución clínica…'} bind:value={notaNueva.contenido} required></textarea>
          {#if SpeechRecognition}
            <button type="button" class="mic" class:on={dictando} onclick={toggleDictado} title="Dictar por voz">
              {dictando ? '⏹ Detener' : '🎤 Dictar'}
            </button>
          {/if}
          <button type="submit" class="primary">Agregar nota</button>
        </form>
        <ul class="timeline">
          {#each notasClinicas as n (n.id)}
            <li>
              <div class="meta"><strong>{n.autor}</strong><time>{n.registradaEn ? new Date(n.registradaEn).toLocaleString('es-GT') : ''}</time></div>
              <p>{n.contenido}</p>
            </li>
          {:else}
            <li class="empty">Sin notas en este expediente.</li>
          {/each}
        </ul>
      {:else if subTab === 'signos'}
        {#if esFisio && paciente?.id}
          <FisioterapiaEvaluacionesPanel
            pacienteId={paciente.id}
            autor={fisioterapistaActual}
            {notas}
            onSaved={async () => { if (paciente) await cargarExpediente(paciente); }}
          />
        {:else}
        <form class="form-signos" onsubmit={registrarSigno}>
          <label>{esFisio ? SIGNOS_FISIO_LABELS.ta : 'TA'} <input bind:value={signoNuevo.ta} placeholder={esFisio ? '7/10' : '120/80'} /></label>
          <label>{esFisio ? SIGNOS_FISIO_LABELS.fc : 'FC'} <input type="number" bind:value={signoNuevo.fc} /></label>
          <label>{esFisio ? SIGNOS_FISIO_LABELS.fr : 'FR'} <input type="number" bind:value={signoNuevo.fr} /></label>
          <label>{esFisio ? SIGNOS_FISIO_LABELS.temp : 'Temp °C'} <input type="number" step="0.1" bind:value={signoNuevo.temp} /></label>
          <label>{esFisio ? SIGNOS_FISIO_LABELS.satO2 : 'Sat O₂ %'} <input type="number" bind:value={signoNuevo.satO2} /></label>
          <label>{esFisio ? SIGNOS_FISIO_LABELS.peso : 'Peso kg'} <input type="number" step="0.1" bind:value={signoNuevo.peso} /></label>
          <label>{esFisio ? SIGNOS_FISIO_LABELS.talla : 'Talla m'} <input type="number" step="0.01" bind:value={signoNuevo.talla} /></label>
          <button type="submit" class="primary">Registrar</button>
        </form>
        <table>
          <thead><tr><th>Fecha</th>{#if esHospital}<th>Origen</th>{/if}<th>{esFisio ? 'EVA' : 'TA'}</th><th>{esFisio ? 'ROM flex.' : 'FC'}</th><th>{esFisio ? 'ROM abd.' : 'FR'}</th><th>{esFisio ? 'MMT' : 'Temp'}</th><th>{esFisio ? 'Func.' : 'Sat'}</th><th>{esFisio ? 'Perim.' : 'Peso'}</th></tr></thead>
          <tbody>
            {#each signosConTriaje as s (s.id)}
              <tr>
                <td>{s.registradoEn ? new Date(s.registradoEn).toLocaleString('es-GT') : '—'}</td>
                {#if esHospital}<td>{s.origen ?? '—'}</td>{/if}
                <td>{s.ta ?? '—'}</td>
                <td>{s.fc ?? '—'}</td>
                <td>{s.fr ?? '—'}</td>
                <td>{s.temp ?? '—'}</td>
                <td>{s.satO2 ?? '—'}</td>
                <td>{s.peso ?? '—'}</td>
              </tr>
            {:else}
              <tr><td colspan={esHospital ? 8 : 7}>Sin signos registrados.</td></tr>
            {/each}
          </tbody>
        </table>
        {/if}
      {:else if subTab === 'rutina' && esFisio}
        <p class="hint">
          Plan de ejercicios domiciliarios prescrito por el fisioterapista. Al guardar queda en el expediente;
          use <strong>Enviar por WhatsApp</strong> para compartirla con el paciente.
          {#if rutinaVersiones > 0}<span class="meta"> · {rutinaVersiones} versión(es) archivada(s)</span>{/if}
        </p>
        <form class="rutina-form" onsubmit={guardarRutina}>
          <label class="wide">Título del plan
            <input bind:value={rutinaEdit.titulo} placeholder="Ej. Plan domiciliar — semana 2" required />
          </label>
          <label class="wide">Indicaciones generales
            <textarea rows="2" bind:value={rutinaEdit.indicaciones} placeholder="Precauciones, frecuencia, cuándo suspender…"></textarea>
          </label>

          <div class="rutina-toolbar">
            <span class="toolbar-label">Plantillas rápidas</span>
            {#each PLANTILLAS_EJERCICIO_FISIO as p, i (i)}
              <button type="button" class="chip" onclick={() => agregarPlantillaRutina(p)}>+ {p.nombre}</button>
            {/each}
            <button type="button" class="chip" onclick={agregarEjercicioRutina}>+ Fila vacía</button>
          </div>

          <div class="rutina-table-wrap">
            <table class="rutina-table">
              <thead>
                <tr>
                  <th>Ejercicio</th><th>Series</th><th>Reps</th><th>Hold</th><th>Frecuencia</th><th>Progresión</th><th></th>
                </tr>
              </thead>
              <tbody>
                {#each rutinaEdit.ejercicios as ej, idx (idx)}
                  <tr>
                    <td><input bind:value={ej.nombre} placeholder="Nombre del ejercicio" required /></td>
                    <td><input type="number" min="1" bind:value={ej.series} class="num" /></td>
                    <td><input bind:value={ej.reps} class="num" /></td>
                    <td><input bind:value={ej.hold} placeholder="30 s" /></td>
                    <td><input bind:value={ej.frecuencia} placeholder="2×/día" /></td>
                    <td><input bind:value={ej.progresion} placeholder="Subir reps…" /></td>
                    <td><button type="button" class="link danger" onclick={() => quitarEjercicioRutina(idx)} title="Quitar">×</button></td>
                  </tr>
                {:else}
                  <tr><td colspan="7" class="empty">Sin ejercicios. Use plantillas rápidas o agregue una fila.</td></tr>
                {/each}
              </tbody>
            </table>
          </div>

          <div class="rutina-actions">
            <button type="submit" class="primary">Guardar rutina</button>
            <button type="button" class="wa" onclick={enviarRutinaWhatsApp} disabled={!paciente.telefono}>
              Enviar por WhatsApp
            </button>
          </div>
        </form>
      {:else if subTab === 'diagnostico'}
        <p class="hint">
          Diagnóstico clínico del expediente. Laboratorio e imagenología de este paciente aparecen abajo.
          Signos, herida y balance quedan en triaje / enfermería.
        </p>
        <form class="ficha-form" onsubmit={guardarDiagnostico}>
          <label class="field full"><span>Diagnóstico</span>
            <div class="dict-row">
              <textarea rows="4" bind:value={fichaEdit.diagnostico} placeholder="Diagnóstico presuntivo o definitivo"></textarea>
              {#if SpeechRecognition}<button type="button" class="mic" class:on={dictandoCampo === 'diagnostico'} onclick={() => toggleDictadoFicha('diagnostico')}>{dictandoCampo === 'diagnostico' ? '⏹' : '🎤'}</button>{/if}
            </div>
          </label>
          <div class="ficha-actions">
            <button type="submit" class="primary" disabled={guardandoFicha}>{guardandoFicha ? 'Guardando…' : 'Guardar diagnóstico'}</button>
          </div>
        </form>

        <h4 class="sub-sec">Laboratorio</h4>
        {#if labOrdenes.length > 0}
          {#each labOrdenes as det (det?.orden?.id)}
            <div class="dx-card">
              <div class="dx-head">
                <strong>{det.orden?.numero ?? 'Orden'}</strong>
                <span class="meta" title={estadoLabHint(det.orden?.estado)}>
                  {det.orden?.medicoSolicitante ?? '—'} · {estadoLabLabel(det.orden?.estado)}
                </span>
              </div>
              {#if det.examenes?.length > 0}
                <table>
                  <thead><tr><th>Examen</th><th>Resultado</th><th>Referencia</th><th>Flag</th></tr></thead>
                  <tbody>
                    {#each det.examenes as ex (ex.id)}
                      <tr>
                        <td>{ex.examenNombre ?? ex.examenCodigo}</td>
                        <td><strong>{ex.valor ?? '—'}</strong> {ex.unidad ?? ''}</td>
                        <td>{ex.valorRefTexto ?? (ex.valorRefMin != null && ex.valorRefMax != null ? `${ex.valorRefMin} - ${ex.valorRefMax}` : '—')}</td>
                        <td>{ex.flag ?? ''}</td>
                      </tr>
                    {/each}
                  </tbody>
                </table>
              {:else}
                <p class="dx-txt">Sin resultados capturados.</p>
              {/if}
            </div>
          {/each}
        {:else}
          <p class="hint">Sin órdenes de laboratorio.</p>
        {/if}

        <h4 class="sub-sec">Imagenología</h4>
        {#if imgOrdenes.length > 0}
          {#each imgOrdenes as det (det.orden?.id ?? det.id)}
            {@const o = det.orden ?? det}
            <div class="dx-card">
              <div class="dx-head">
                <strong>{o.numero ?? 'Orden'}</strong>
                <span class="meta" title={estadoImagenHint(o.estado)}>
                  {o.medicoSolicitante ?? '—'} · {estadoImagenLabel(o.estado)}
                </span>
              </div>
              {#if det.estudios?.length}
                <p class="dx-txt"><strong>Estudios:</strong> {det.estudios.map((e: any) => e.estudioNombre).join(', ')}</p>
              {/if}
              {#if o.indicacionClinica}<p class="dx-txt"><strong>Indicación:</strong> {o.indicacionClinica}</p>{/if}
              {#if o.hallazgos}<p class="dx-txt"><strong>Hallazgos:</strong> {o.hallazgos}</p>{/if}
              {#if o.impresion}<p class="dx-txt"><strong>Impresión:</strong> {o.impresion}</p>{/if}
              {#if o.radiologo}<small class="meta">Informado por {o.radiologo}</small>{/if}
            </div>
          {/each}
        {:else}
          <p class="hint">Sin estudios de imagenología.</p>
        {/if}

      {:else if subTab === 'receta'}
        <p class="hint">
          Emite la receta desde la consulta. Se vincula al médico en sesión
          {#if medicoActual}(<strong>{medicoActual}</strong>){/if}
          y la farmacia la despacha en el POS.
        </p>
        <form class="form-receta" onsubmit={emitirReceta}>
          <label>Diagnóstico <input bind:value={recetaDiagnostico} placeholder="Se completa desde el diagnóstico del expediente" /></label>
          <button type="button" onclick={agregarItemReceta}>+ Medicamento</button>
          {#if recetaItems.length > 0}
            <table>
              <thead><tr><th>Medicamento</th><th>Cant.</th><th>Indicaciones</th><th></th></tr></thead>
              <tbody>
                {#each recetaItems as it, i (i)}
                  <tr>
                    <td><input bind:value={it.descripcion} required placeholder="Nombre / dosis" /></td>
                    <td><input type="number" min="1" bind:value={it.cantidad} class="num" /></td>
                    <td><input bind:value={it.indicaciones} placeholder="Posología" /></td>
                    <td><button type="button" onclick={() => quitarItemReceta(i)}>×</button></td>
                  </tr>
                {/each}
              </tbody>
            </table>
          {/if}
          <button type="submit" class="primary" disabled={recetaItems.length === 0}>Emitir receta</button>
        </form>
        <h4>Recetas del paciente</h4>
        <table>
          <thead><tr><th>Fecha</th><th>Diagnóstico</th><th>Estado</th><th></th></tr></thead>
          <tbody>
            {#each recetasPaciente as r (r.id)}
              <tr>
                <td>{r.fecha?.slice(0, 10) ?? '—'}</td>
                <td>{r.diagnostico ?? '—'}</td>
                <td>{r.estado}</td>
                <td>
                  {#if r.estado !== 'ANULADA'}
                    <button type="button" onclick={() => verReceta(r)}>Vista previa</button>
                    <button type="button" onclick={() => imprimirReceta(r)}>Imprimir ℞</button>
                    <button type="button" class="wa" onclick={() => enviarRecetaWhatsApp(r)}>WhatsApp</button>
                  {/if}
                </td>
              </tr>
              {:else}
              <tr><td colspan="4">Sin recetas en este expediente.</td></tr>
            {/each}
          </tbody>
        </table>

      {:else if subTab === 'medicamentos'}
        {#if esHospital}
          <p class="hint">
            Indique el tratamiento del paciente hospitalizado. El buscador muestra los <strong>10 más recientes</strong>; escriba para filtrar.
          </p>
          <form class="form-tratamiento" onsubmit={guardarTratamientoHospital}>
            {#if medsHospital.length === 0}
              <p class="hint warn">
                Primero cargue el catálogo en <strong>Farmacia hospitalaria → Medicamentos</strong>.
                Es el mismo paso para todo hospital nuevo; sin medicamentos en catálogo no se puede programar el kárdex.
              </p>
            {/if}
            <label class="med-picker-wrap">Medicamento *
              <div class="med-picker">
                <input
                  type="text"
                  placeholder="Buscar medicamento…"
                  bind:value={medBusqueda}
                  onfocus={() => medPickerOpen = true}
                  oninput={() => { medPickerOpen = true; }}
                  autocomplete="off"
                />
                {#if tratamientoNuevo.medicamentoId && tratamientoNuevo.descripcion}
                  <p class="med-selected">Seleccionado: <strong>{tratamientoNuevo.descripcion}</strong></p>
                {/if}
                {#if medPickerOpen && medsFiltrados.length > 0}
                  <ul class="med-dropdown">
                    {#each medsFiltrados as m (m.id)}
                      <li>
                        <button type="button" onclick={() => seleccionarMedicamento(m)}>{m.nombre}</button>
                      </li>
                    {/each}
                  </ul>
                {:else if medPickerOpen && medBusqueda.trim() && medsFiltrados.length === 0}
                  <p class="med-empty">Sin coincidencias en el catálogo.</p>
                {/if}
              </div>
            </label>
            <label>Dosis * <input bind:value={tratamientoNuevo.dosis} placeholder="Ej. 1 g" required /></label>
            <label>Vía
              <select bind:value={tratamientoNuevo.via}>
                <option value="IV">IV</option>
                <option value="VO">VO</option>
                <option value="IM">IM</option>
                <option value="SC">SC</option>
                <option value="INH">INH</option>
              </select>
            </label>
            <label>Frecuencia <input bind:value={tratamientoNuevo.frecuencia} placeholder="c/8h" /></label>
            <label>Primera dosis (hora) *
              <input type="datetime-local" bind:value={tratamientoNuevo.horaProgramada} required />
            </label>
            <button type="submit" class="primary" disabled={guardandoTratamiento}>
              {guardandoTratamiento ? 'Guardando…' : 'Indicar y programar en kárdex'}
            </button>
          </form>
          {#if indicacionesHospital.length > 0}
            <h4 class="sub-sec">Indicaciones activas</h4>
            <table>
              <thead><tr><th>Medicamento</th><th>Dosis</th><th>Vía</th><th>Frecuencia</th><th>Médico</th></tr></thead>
              <tbody>
                {#each indicacionesHospital as i (i.id)}
                  <tr>
                    <td>{i.descripcion}</td>
                    <td>{i.dosis ?? '—'}</td>
                    <td>{i.via ?? '—'}</td>
                    <td>{i.frecuencia ?? '—'}</td>
                    <td>{i.indicadaPor ?? '—'}</td>
                  </tr>
                {/each}
              </tbody>
            </table>
          {/if}
        {/if}
        <p class="hint">Medicamentos administrados y programados (kárdex hospitalario).</p>
        {#if kardex.length > 0}
          <table>
            <thead><tr><th>Medicamento</th><th>Dosis</th><th>Vía</th><th>Programada</th><th>Estado</th></tr></thead>
            <tbody>
              {#each kardex as k (k.id)}
                <tr>
                  <td>{nombreMedicamento(k)}</td>
                  <td>{k.dosis ?? '—'}</td>
                  <td>{k.via ?? '—'}</td>
                  <td>{k.horaProgramada ? new Date(k.horaProgramada).toLocaleString('es-GT') : '—'}</td>
                  <td>{k.estado}</td>
                </tr>
              {/each}
            </tbody>
          </table>
        {:else}
          <p class="hint">Sin medicamentos registrados en kárdex.</p>
        {/if}

      {:else if subTab === 'citas'}
        {#if citasPaciente.length > 0}
          <table>
            <thead><tr><th>Fecha y hora</th><th>Motivo</th><th>Médico</th><th>Consultorio</th><th>Estado</th></tr></thead>
            <tbody>
              {#each citasPaciente as c (c.id)}
                <tr>
                  <td>{c.fechaHora ? new Date(c.fechaHora).toLocaleString('es-GT') : '—'}</td>
                  <td>{c.motivo ?? '—'}</td>
                  <td>{c.medico ?? '—'}</td>
                  <td>{c.consultorio ?? '—'}</td>
                  <td>{c.estado ?? '—'}</td>
                </tr>
              {/each}
            </tbody>
          </table>
        {:else}
          <p class="hint">Sin citas registradas.</p>
        {/if}

      {:else if subTab === 'altas'}
        {#if altasPaciente.length > 0}
          {#each altasPaciente as a (a.id)}
            <div class="dx-card">
              <div class="dx-head">
                <strong>Alta · {a.fechaAlta ? new Date(a.fechaAlta).toLocaleDateString('es-GT') : '—'}</strong>
                <span class="meta">Condición: {a.condicionEgreso ?? '—'} · {a.firmadaPor ?? '—'}</span>
              </div>
              <p class="dx-txt"><strong>Diagnóstico de egreso:</strong> {a.diagnosticoEgreso ?? '—'}</p>
              {#if a.indicaciones}<p class="dx-txt"><strong>Indicaciones:</strong> {a.indicaciones}</p>{/if}
              {#if a.recetas}<p class="dx-txt"><strong>Recetas:</strong> {a.recetas}</p>{/if}
              {#if a.cuidadosEnCasa}<p class="dx-txt"><strong>Cuidados en casa:</strong> {a.cuidadosEnCasa}</p>{/if}
              {#if a.proximaCita}<p class="dx-txt"><strong>Próxima cita:</strong> {a.proximaCita}</p>{/if}
            </div>
          {/each}
        {:else}
          <p class="hint">Sin altas hospitalarias registradas.</p>
        {/if}

      {:else if subTab === 'adjuntos'}
        <p class="hint">Suba laboratorios, radiografías o reportes (PDF / imagen) realizados interna o externamente.</p>
        <form class="form-grid" onsubmit={guardarAdjunto}>
          <label>Título <input bind:value={adjuntoNuevo.titulo} required placeholder="Ej. Rx tórax, hemograma externo" /></label>
          <label>Origen
            <select bind:value={adjuntoNuevo.origen}><option value="EXTERNO">Externo</option><option value="INTERNO">Interno</option></select>
          </label>
          <label>Tipo
            <select bind:value={adjuntoNuevo.tipo}>
              <option value="LABORATORIO">Laboratorio</option>
              <option value="RADIOGRAFIA">Radiografía / imagen</option>
              <option value="REPORTE">Reporte PDF</option>
              <option value="OTRO">Otro</option>
            </select>
          </label>
          <label>Archivo <input type="file" accept="image/*,.pdf,application/pdf" onchange={onArchivoAdjunto} /></label>
          <label class="span-2">Nota <input bind:value={adjuntoNuevo.nota} placeholder="Opcional" /></label>
          <button type="submit" class="primary">Guardar en expediente</button>
        </form>
        <table>
          <thead><tr><th>Fecha</th><th>Título</th><th>Origen</th><th>Tipo</th><th></th></tr></thead>
          <tbody>
            {#each adjuntos as a (a.id)}
              <tr>
                <td>{a.fecha}</td>
                <td>{a.titulo}{#if a.dataUrl} · <a href={a.dataUrl} target="_blank" rel="noreferrer">{a.archivoNombre || 'ver'}</a>{/if}</td>
                <td>{a.origen}</td>
                <td>{a.tipo}</td>
                <td><button type="button" onclick={() => quitarAdjunto(a.id)}>Quitar</button></td>
              </tr>
            {:else}
              <tr><td colspan="5" class="empty">Sin estudios adjuntos.</td></tr>
            {/each}
          </tbody>
        </table>

      {:else if subTab === 'postop' && esHospital}
        {@const notasPost = notas.filter(n => n.tipo === 'POSTOP')}
        <p class="hint">
          Nota breve del médico: fecha/hora, evolución y medicamento postoperatorio.
          Signos, herida y balance ya quedan en el triaje de enfermería.
        </p>
        <form class="form-grid" onsubmit={guardarPostop}>
          <label>Fecha y hora <input type="datetime-local" bind:value={postop.fechaHora} /></label>
          <label class="wide">Evolución postoperatoria
            <textarea rows="3" bind:value={postop.evolucion} placeholder="Evolución clínica post-op…"></textarea>
          </label>
          <label class="wide">Medicamento postoperatorio
            <textarea rows="2" bind:value={postop.medicamento} placeholder="Analgésicos, antibióticos, dosis y vía…"></textarea>
          </label>
          <button type="submit" class="primary">Registrar nota post-op</button>
        </form>
        {#each notasPost as n (n.id)}
          <div class="dx-card" class:alerta={n.contenido.includes('ALERTA')}>
            <div class="dx-head"><strong>{n.autor}</strong><span class="meta">{n.registradaEn ? new Date(n.registradaEn).toLocaleString('es-GT') : ''}</span></div>
            <p class="dx-txt" style="white-space:pre-wrap">{n.contenido}</p>
          </div>
        {:else}
          <p class="hint">Sin notas postquirúrgicas.</p>
        {/each}

      {:else if subTab === 'placas' && esDental}
        <p class="hint">
          La mayoría de consultorios odontológicos toman radiografías intraorales o panorámicas en sitio.
          Las placas quedan vinculadas al expediente del paciente (CUI).
        </p>
        <form class="form-placa" onsubmit={guardarPlaca}>
          <label>Tipo
            <select bind:value={placaNueva.tipo}>
              {#each TIPOS_PLACA as t}<option value={t.value}>{t.label}</option>{/each}
            </select>
          </label>
          <label>Diente(s) / región <input bind:value={placaNueva.dientes} placeholder="Ej. 18, 17-16 o cuadrante" required /></label>
          <label>Odontólogo <input bind:value={placaNueva.medico} placeholder="Dr(a). …" /></label>
          <label>Observaciones <input bind:value={placaNueva.nota} placeholder="Hallazgo, indicación…" /></label>
          <label class="file">
            Imagen / placa
            <input type="file" accept="image/*,.dcm" onchange={onArchivoPlaca} />
          </label>
          <button type="submit" class="primary">Archivar placa</button>
        </form>

        <div class="placas-grid">
          {#each placas as p (p.id)}
            <article class="placa-card">
              {#if p.imagenUrl}
                <img src={p.imagenUrl} alt={`Placa ${p.dientes}`} />
              {:else}
                <div class="placa-placeholder">🩻 {p.tipo}</div>
              {/if}
              <div class="placa-meta">
                <strong>{p.dientes}</strong>
                <span>{p.tipo} · {p.fecha}</span>
                <span>{p.medico}</span>
                {#if p.nota}<p>{p.nota}</p>{/if}
                {#if p.archivoNombre}<small>{p.archivoNombre}</small>{/if}
                <button type="button" class="link danger" onclick={() => eliminarPlaca(p.id)}>Eliminar</button>
              </div>
            </article>
          {:else}
            <p class="hint">Sin placas archivadas. Use el formulario para registrar la primera radiografía.</p>
          {/each}
        </div>
      {/if}
    </div>
  {/if}
</section>

<style>
  .expediente { padding: 1rem; max-width: 960px; }
  header h2 { margin: 0 0 .35rem; font-size: 1.4rem; color: #111827; }
  .hint { color: #6b7280; font-size: .9rem; margin: 0 0 .75rem; }
  .demo-banner {
    display: flex; flex-direction: column; gap: .25rem;
    padding: .65rem .85rem; margin-bottom: .75rem;
    border-radius: 10px; border: 1px dashed #16a34a; background: #f0fdf4;
    font-size: .88rem; color: #166534;
  }
  .demo-banner strong { font-size: .78rem; text-transform: uppercase; letter-spacing: .04em; }
  .cui-link {
    border: 0; background: none; color: #16a34a; font-weight: 800; cursor: pointer;
    text-decoration: underline; padding: 0 .15rem;
  }
  .demo-more { font-size: .8rem; color: #15803d; }
  .buscar-bar { display: flex; gap: .65rem; flex-wrap: wrap; align-items: end; margin-bottom: 1rem; }
  .buscar-bar .grow { flex: 1; min-width: 240px; }
  .cui-bar { display: flex; gap: .65rem; flex-wrap: wrap; align-items: end; flex: 1; min-width: 280px; }
  .cui-field { display: flex; flex-direction: column; gap: .25rem; flex: 1; min-width: 220px; }
  .cui-field span { font-size: .72rem; font-weight: 700; color: #6b7280; text-transform: uppercase; letter-spacing: .04em; }
  .paciente-meta .admision { color: #166534; font-weight: 600; }
  input, select, textarea, button { font: inherit; border-radius: 8px; border: 1px solid #e5e7eb; padding: .5rem .65rem; background: #fff; color: #111827; }
  input:focus, select:focus, textarea:focus { outline: none; border-color: #16a34a; box-shadow: 0 0 0 3px rgba(22,163,74,0.12); }
  button { cursor: pointer; background: #f3f4f6; }
  button.primary { background: linear-gradient(135deg, #15803d, #16a34a); color: #fff; border: 0; font-weight: 600; box-shadow: 0 4px 14px rgba(22,163,74,0.3); transition: transform 0.15s; }
  button.primary:hover:not(:disabled) { transform: translateY(-1px); }
  button.primary:disabled { opacity: .55; box-shadow: none; }
  .readonly { background: #f5f5f5; color: #6b7280; }
  .err { background: #fee2e2; color: #dc2626; padding: .6rem; border-radius: 8px; margin: .5rem 0; }
  .ok { background: #dcfce7; color: #166534; padding: .6rem; border-radius: 8px; margin: .5rem 0; }
  .dx-card.alerta { border-color: #f59e0b; background: #fffbeb; }
  .alta-card, .paciente-card { border: 1px solid #e5e7eb; border-radius: 12px; padding: 1rem; background: #fff; margin-top: .75rem; box-shadow: 0 1px 3px rgb(0 0 0 / 5%); }
  .alta-card h3 { color: #166534; }
  .form-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: .65rem; }
  .form-grid label { display: flex; flex-direction: column; gap: .25rem; font-size: .72rem; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: .03em; }
  .form-grid .wide { grid-column: 1 / -1; }
  .form-grid textarea { font: inherit; padding: .5rem .65rem; border: 1px solid #e5e7eb; border-radius: 8px; text-transform: none; font-weight: 400; color: #111827; }
  .paciente-head { display: flex; justify-content: space-between; align-items: flex-start; flex-wrap: wrap; gap: .5rem; margin-bottom: .75rem; }
  .cui-badge { display: inline-block; margin-left: .5rem; padding: .15rem .5rem; background: #dcfce7; color: #166534; border-radius: 999px; font-size: .78rem; font-weight: 700; }
  .paciente-meta { display: flex; gap: .9rem; flex-wrap: wrap; margin-top: .3rem; font-size: .82rem; color: #6b7280; }
  .print-btn { background: #fff; color: #15803d; border: 1px solid #16a34a; font-weight: 600; padding: .5rem .9rem; cursor: pointer; }
  .print-btn:hover { background: #f0fdf4; }
  .meta { color: #6b7280; font-size: .85rem; }
  .subtabs { display: flex; gap: .35rem; flex-wrap: wrap; margin-bottom: 1rem; }
  .subtabs button { border: 1px solid #e5e7eb; background: #fff; padding: .4rem .75rem; color: #15803d; }
  .subtabs button.active { background: #16a34a; color: #fff; border-color: #16a34a; }
  .resumen-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(140px, 1fr)); gap: .65rem; margin-bottom: 1rem; }
  .stat { background: #fff; border: 1px solid #e5e7eb; border-radius: 12px; padding: .65rem .75rem; box-shadow: 0 1px 3px rgb(0 0 0 / 5%); }
  .stat span { display: block; font-size: .75rem; color: #6b7280; }
  .stat strong { font-size: 1.35rem; color: #166534; }
  .stat.wide { grid-column: 1 / -1; }
  .preview-nota { background: #fff; border-left: 4px solid #16a34a; padding: .65rem .85rem; border-radius: 0 8px 8px 0; }
  .preview-nota p { margin: .35rem 0 0; white-space: pre-wrap; color: #111827; }
  .sub-sec { margin: 1.25rem 0 .5rem; font-size: .95rem; color: #166534; }
  .dx-card { background: #fff; border: 1px solid #e5e7eb; border-radius: 10px; padding: .75rem .9rem; margin-bottom: .65rem; box-shadow: 0 1px 3px rgb(0 0 0 / 5%); }
  .dx-head { display: flex; justify-content: space-between; align-items: baseline; gap: .5rem; flex-wrap: wrap; margin-bottom: .35rem; }
  .dx-head strong { color: #111827; }
  .dx-txt { margin: .25rem 0; font-size: .88rem; white-space: pre-wrap; }
  .form-inline { display: flex; flex-direction: column; gap: .5rem; margin-bottom: 1rem; }
  .timeline { list-style: none; padding: 0; margin: 0; }
  .timeline li { border: 1px solid #e5e7eb; border-radius: 10px; padding: .75rem; margin-bottom: .5rem; background: #fff; box-shadow: 0 1px 3px rgb(0 0 0 / 5%); }
  .timeline .meta { display: flex; justify-content: space-between; font-size: .82rem; color: #6b7280; margin-bottom: .25rem; }
  .timeline p { margin: 0; white-space: pre-wrap; color: #111827; }
  .empty { color: #9ca3af; font-style: italic; }
  .form-signos { display: flex; flex-wrap: wrap; gap: .5rem; align-items: end; margin-bottom: 1rem; }
  .form-receta { display: flex; flex-direction: column; gap: .65rem; margin-bottom: 1rem; max-width: 40rem; }
  .form-tratamiento { display: grid; grid-template-columns: repeat(3, 1fr); gap: .65rem; margin-bottom: 1rem; max-width: 52rem; align-items: end; }
  .form-tratamiento label { display: flex; flex-direction: column; gap: .25rem; font-size: .85rem; color: #111827; }
  .form-tratamiento button.primary { grid-column: 1 / -1; justify-self: start; }
  .med-picker-wrap { grid-column: 1 / -1; position: relative; }
  .med-picker { position: relative; }
  .med-picker input { width: 100%; box-sizing: border-box; }
  .med-selected { margin: .35rem 0 0; font-size: .82rem; color: #166534; }
  .med-dropdown { list-style: none; margin: .25rem 0 0; padding: 0; border: 1px solid #e5e7eb; border-radius: 8px; background: #fff; max-height: 220px; overflow-y: auto; box-shadow: 0 4px 12px rgb(0 0 0 / 8%); z-index: 5; position: absolute; left: 0; right: 0; }
  .med-dropdown li button { display: block; width: 100%; text-align: left; padding: .45rem .65rem; border: 0; background: transparent; color: #111827; cursor: pointer; font: inherit; box-shadow: none; border-radius: 0; }
  .med-dropdown li button:hover { background: #f0fdf4; color: #166534; }
  .med-empty { margin: .35rem 0 0; font-size: .82rem; color: #9ca3af; font-style: italic; }
  .form-receta label { display: flex; flex-direction: column; gap: .25rem; font-size: .85rem; color: #111827; }
  .form-receta table { width: 100%; margin: .25rem 0; }
  .form-receta .num { width: 4rem; }
  .form-signos label { display: flex; flex-direction: column; gap: .2rem; font-size: .8rem; color: #6b7280; }
  .form-signos input { width: 5.5rem; }
  table { width: 100%; border-collapse: collapse; }
  th { border-bottom: 1px solid #e5e7eb; padding: .5rem .8rem; text-align: left; font-size: .7rem; text-transform: uppercase; letter-spacing: .03em; color: #6b7280; background: #f0fdf4; }
  td { border-top: 1px solid #f3f4f6; padding: .4rem .8rem; text-align: left; font-size: .88rem; color: #111827; }
  .form-placa { display: grid; grid-template-columns: repeat(auto-fill, minmax(180px, 1fr)); gap: .65rem; margin-bottom: 1rem; }
  .form-placa label { display: flex; flex-direction: column; gap: .25rem; font-size: .85rem; color: #111827; }
  .file input[type="file"] { padding: .35rem; }
  .placas-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(220px, 1fr)); gap: .75rem; }
  .placa-card { border: 1px solid #e5e7eb; border-radius: 12px; overflow: hidden; background: #fff; box-shadow: 0 1px 3px rgb(0 0 0 / 5%); }
  .placa-card img { width: 100%; height: 140px; object-fit: cover; background: #111; }
  .placa-placeholder { height: 140px; display: grid; place-items: center; background: #166534; color: #dcfce7; font-size: 1.1rem; }
  .placa-meta { padding: .55rem .65rem; font-size: .82rem; display: flex; flex-direction: column; gap: .15rem; }
  .placa-meta strong { color: #111827; }
  .placa-meta p { margin: .25rem 0 0; color: #6b7280; }
  .link { background: none; border: 0; color: #16a34a; padding: .25rem 0; align-self: flex-start; box-shadow: none; }
  .link.danger { color: #dc2626; }
  .rutina-form { display: flex; flex-direction: column; gap: .75rem; }
  .rutina-form .wide { display: flex; flex-direction: column; gap: .25rem; font-size: .85rem; color: #111827; }
  .rutina-toolbar { display: flex; flex-wrap: wrap; gap: .35rem; align-items: center; }
  .toolbar-label { font-size: .78rem; font-weight: 700; color: #6b7280; margin-right: .25rem; }
  .chip { font-size: .78rem; padding: .3rem .6rem; border-radius: 6px; border: 1px solid #e5e7eb; background: #fff; color: #166534; cursor: pointer; font-weight: 500; }
  .chip:hover { background: #f0fdf4; border-color: #16a34a; }
  .rutina-table-wrap { overflow-x: auto; }
  .rutina-table { width: 100%; border-collapse: collapse; font-size: .82rem; }
  .rutina-table th { padding: .35rem; background: #f0fdf4; color: #6b7280; font-size: .7rem; text-transform: uppercase; border-bottom: 1px solid #e5e7eb; vertical-align: middle; }
  .rutina-table td { padding: .35rem; border-top: 1px solid #f3f4f6; vertical-align: middle; color: #111827; }
  .rutina-table input { width: 100%; min-width: 4rem; padding: .35rem .45rem; font-size: .82rem; }
  .rutina-table .num { max-width: 4rem; }
  .rutina-actions { display: flex; flex-wrap: wrap; gap: .5rem; margin-top: .25rem; }
  button.wa { background: #16a34a; color: #fff; border: 0; padding: .5rem .85rem; border-radius: 8px; font-weight: 700; }
  button.wa:disabled { opacity: .45; cursor: not-allowed; }
  button.mic { background: #fff; color: #0f766e; border: 1px solid #14b8a6; padding: .5rem .85rem; border-radius: 8px; font-weight: 700; cursor: pointer; }
  button.mic.on { background: #dc2626; color: #fff; border-color: #dc2626; }
  .ficha-form { display: flex; flex-direction: column; gap: .6rem; }
  .ficha-form .field { display: flex; flex-direction: column; gap: .3rem; }
  .ficha-form .field span { font-size: .72rem; font-weight: 600; color: #6b7280; text-transform: uppercase; letter-spacing: .03em; }
  .ficha-form .full { width: 100%; }
  .ficha-form textarea { width: 100%; box-sizing: border-box; resize: vertical; }
  .dict-row { display: flex; gap: .4rem; align-items: flex-start; }
  .dict-row textarea { flex: 1; }
  .dict-row .mic { flex-shrink: 0; margin-top: .2rem; }
  .ficha-cols { display: grid; grid-template-columns: 1fr 1fr; gap: .6rem; }
  .ficha-actions { display: flex; justify-content: flex-end; gap: .5rem; }
  @media (max-width: 620px) { .ficha-cols { grid-template-columns: 1fr; } }
</style>
